<template>
  <v-form ref="form" class="task-form" @submit.prevent="submitTask">
    <v-text-field
      v-model="taskEntry.task"
      placeholder="Que faut-il faire ?"
      aria-label="Intitulé de la tâche"
      variant="underlined"
      class="task-form__title"
      :rules="$fieldsRules.required('tâche', true)"
      autofocus
    />

    <div class="task-form__editor">
      <vue-editor
        v-model:content="taskEntry.content"
        content-type="html"
        theme="snow"
        placeholder="Ajoutez des détails, un lien, une consigne…"
        :toolbar="toolbar"
      />
    </div>

    <div class="task-form__pills">
      <TaskPicker v-model="taskEntry.status" label="Statut" icon="mdi-progress-clock" :items="statusItems" />
      <TaskPicker v-model="taskEntry.priority" label="Priorité" icon="mdi-flag-outline" :items="priorityItems" />
      <TaskPicker v-model="taskEntry.category" label="Catégorie" icon="mdi-tag-outline" :items="categoryItems" />

      <v-menu v-model="dateMenu" :close-on-content-click="false" location="bottom start" :offset="6" content-class="k-glass-3">
        <template v-slot:activator="{ props }">
          <button v-bind="props" type="button" class="task-form__date" :class="{ 'task-form__date--set': taskEntry.dueDate }">
            <v-icon icon="mdi-calendar-outline" size="16" />
            <span>{{ dueLabel }}</span>
            <v-icon
              v-if="taskEntry.dueDate"
              icon="mdi-close"
              size="14"
              role="button"
              aria-label="Retirer l'échéance"
              @click.stop="clearDueDate"
            />
          </button>
        </template>
        <div class="task-form__calendar">
          <v-date-picker :model-value="taskEntry.dueDate || undefined" hide-header @update:model-value="onPickDueDate" />
          <v-text-field v-if="taskEntry.dueDate" v-model="time" type="time" label="Heure limite" density="compact" variant="outlined" hide-details />
        </div>
      </v-menu>

      <TaskPicker
        v-model="taskEntry.project"
        label="Projet"
        icon="mdi-folder-outline"
        :items="projectItems"
        searchable
        clearable
        clearLabel="Aucun projet"
      />
      <TaskPicker
        v-model="taskEntry.participants"
        label="Participants"
        icon="mdi-account-plus-outline"
        :items="peopleItems"
        multiple
        searchable
      />
      <TaskPicker
        v-if="!edit && companyItems.length > 1"
        v-model="company"
        label="Entreprise"
        icon="mdi-domain"
        :items="companyItems"
      />
    </div>

    <div class="task-form__actions">
      <v-btn v-if="edit" variant="text" color="error" class="text-none mr-auto" @click="$emit('delete')">
        Supprimer
      </v-btn>
      <v-btn v-if="showCancel" variant="outlined" class="text-none" @click="$emit('cancel')">
        Annuler
      </v-btn>
      <v-btn color="primary" class="text-none" type="submit" :loading="saving">
        {{ edit ? "Enregistrer" : "Ajouter la tâche" }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import { setSnackbar } from "@/stores/ui";
import { QuillEditor as VueEditor } from "@vueup/vue-quill";
import TaskPicker from "@/components/modules/@tasks/TaskPicker";
import { cloneDeep } from "@/utils/cloneDeep";
import { capture } from "@/posthog";
import { mapStores } from "pinia";
import { useTasksStore } from "@/stores/tasks";
import { useCompanyDirectory } from "@/composables/useCompanyDirectory";

const TOOLBAR = [["bold", "italic", "underline", "link"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]];

export default {
  name: "ComponentTaskForm",
  props: {
    userInfo: {
      type: Object,
      required: true,
    },
    task: {
      type: Object,
      default: null,
    },
    showCancel: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["created", "saved", "cancel", "delete"],
  components: {
    VueEditor,
    TaskPicker,
  },
  setup() {
    const { clients, companies, members, projects, start } = useCompanyDirectory();
    return { clients, directoryCompanies: companies, members, projects, startDirectory: start };
  },
  data() {
    return {
      dateMenu: false,
      time: "23:59",
      taskEntry: this.blankTask(),
      saving: false,
      toolbar: TOOLBAR,
      company: "",
      companies: [],
    };
  },
  computed: {
    people() {
      return [...this.members, ...this.clients, ...this.directoryCompanies];
    },
    ...mapStores(useTasksStore),
    edit() {
      return !!this.task;
    },
    targetCompany() {
      return this.edit ? this.userInfo.company : this.company;
    },
    statusItems() {
      return this.$listUtils.elementStatus.map((title, value) => ({ title, value }));
    },
    priorityItems() {
      return this.$listUtils.priority.map((title, value) => ({ title, value }));
    },
    categoryItems() {
      return this.$listUtils.itemCategory.map((cat, value) => ({ title: cat.label, value }));
    },
    projectItems() {
      return this.projects.map((project) => ({
        value: project.id,
        title: project.name,
        image: project.avatar ? this.$functions.getImgLink(project.avatar, "small") : "",
        initials: project.avatar ? "" : String(project.name || "?")[0],
      }));
    },
    companyItems() {
      return this.companies.map((company) => ({ value: company.id, title: company.name }));
    },
    peopleItems() {
      return this.people
        .filter((person) => person.type !== "subheader")
        .map((person) => ({
          value: person.id,
          title: person.name,
          subtitle: !person.denom || person.denom === "utilisateur" ? "Utilisateur" : `${person.denom} ${person.content ?? ""}`.trim(),
          image: person.avatar ? this.$functions.getImgLink(person.avatar, "small") : "",
          initials: person.avatar ? "" : this.$functions.getInitials(person.name || "?"),
        }));
    },
    dueLabel() {
      if (!this.taskEntry.dueDate) return "Échéance";
      return `${this.$moment(this.taskEntry.dueDate).format("DD/MM/YYYY")} ${this.time}`;
    },
    dateTime() {
      const date = this.$moment(this.taskEntry.dueDate).format("YYYY-MM-DD");
      return new Date(`${date} ${this.time || "23:59"}`);
    },
  },
  methods: {
    blankTask() {
      return {
        ...this.$models.task,
        task: "",
        content: "",
        category: 0,
        project: null,
        participants: [],
        dueDate: "",
        priority: 2,
        status: 1,
      };
    },
    fromTask(task) {
      const entry = cloneDeep(task);
      let due = task.dueDate || null;
      if (due && typeof due.toDate === "function") due = due.toDate();
      entry.status = Number(task.status);
      entry.priority = Number(task.priority);
      entry.category = Number(task.category);
      entry.project = task.project || null;
      entry.participants = [...(task.participants || [])];
      entry.dueDate = due ? this.$moment(due).format("YYYY-MM-DD") : "";
      this.time = due ? this.$moment(due).format("HH:mm") : "23:59";
      return entry;
    },
    onPickDueDate(val) {
      if (!val) return;
      const raw = Array.isArray(val) ? val[0] : val;
      this.taskEntry.dueDate = this.$moment(raw).format("YYYY-MM-DD");
    },
    clearDueDate() {
      this.taskEntry.dueDate = "";
    },
    resetForm() {
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.taskEntry = this.blankTask();
      this.time = "23:59";
    },
    async submitTask() {
      const validation = await this.$refs.form.validate();
      if (validation === false || validation?.valid === false) return;
      this.saving = true;
      try {
        if (this.edit) await this.saveEdit();
        else await this.saveNew();
      } finally {
        this.saving = false;
      }
    },
    async saveNew() {
      const task = {
        ...this.taskEntry,
        category: Number(this.taskEntry.category),
        creationDate: new Date(),
        lastUpdate: new Date(),
        author: this.userInfo.id,
        content: this.taskEntry.content || "",
      };

      if (!task.participants || task.participants.length === 0) delete task.participants;
      if (!task.project) delete task.project;
      if (this.taskEntry.dueDate) task.dueDate = this.dateTime;
      else delete task.dueDate;

      await this.$api.apiUser({
        method: "post",
        url: `/companies/${this.company}/tasks`,
        data: task,
      });

      setSnackbar({ text: `La tâche ${task.task} a été ajoutée !` });

      /* eslint-disable camelcase */
      capture("task_created", {
        category: task.category,
        priority: task.priority,
        status: task.status,
        has_project: !!task.project,
        has_due_date: !!task.dueDate,
        participant_count: task.participants?.length || 0,
      });
      /* eslint-enable camelcase */

      this.resetForm();
      this.$emit("created");
    },
    async saveEdit() {
      const data = {
        ...this.taskEntry,
        author: this.taskEntry.author?.id || this.taskEntry.author,
        category: Number(this.taskEntry.category),
        lastUpdate: new Date(),
        content: this.taskEntry.content || "",
      };

      delete data.id;
      delete data.time;
      delete data.participantsData;
      delete data.projectData;
      delete data.authorId;
      if (data.status === Number(this.task.status)) delete data.status;
      if (!data.project) delete data.project;
      if (this.taskEntry.dueDate) data.dueDate = this.dateTime;
      else delete data.dueDate;

      await this.$api.apiUser({
        method: "patch",
        url: `/companies/${this.userInfo.company}/tasks/${this.task.id}`,
        data,
      });

      setSnackbar({ text: `La tâche ${data.task} a été modifiée !` });
      this.$emit("saved");
    },
    init() {
      this.startDirectory(this.targetCompany);
    },
    loadCompanies() {
      this.companies = [];
      this.company = this.userInfo.company;
      this.$api
        .apiUser({
          method: "get",
          url: `/users/${this.userInfo.id}/companies`,
        })
        .then((res) => {
          this.companies = (res.data.documents || [])
            .slice()
            .sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()));
        });
    },
  },
  watch: {
    people(list) {
      this.tasksStore.seedPeople(list);
    },
    task: {
      immediate: true,
      handler(task) {
        this.taskEntry = task ? this.fromTask(task) : this.blankTask();
      },
    },
    userInfo: {
      deep: true,
      immediate: true,
      handler(info) {
        if (!info?.company) return;
        this.company = info.company;
        if (!this.edit) this.loadCompanies();
        this.init();
      },
    },
    company(val, oldVal) {
      if (val && val !== oldVal) this.init();
    },
  },
};
</script>

<style scoped>
.task-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.task-form__title :deep(input) {
  font-size: 22px;
  font-weight: 300;
}

.task-form__editor {
  border: 1px solid var(--k-glass-border);
  border-radius: var(--k-radius-md);
  background: var(--k-glass-1-bg);
  overflow: hidden;
}

.task-form__editor :deep(.ql-toolbar.ql-snow) {
  padding: 4px 8px;
  border: 0;
  border-bottom: 1px solid var(--k-glass-border);
}

.task-form__editor :deep(.ql-container.ql-snow) {
  border: 0;
}

.task-form__editor :deep(.ql-editor) {
  min-height: 84px;
  max-height: 180px;
  font-size: 14px;
}

.task-form__pills {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.task-form__date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--k-glass-border);
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  cursor: pointer;
}

.task-form__date:hover {
  background: var(--k-track);
}

.task-form__date--set {
  border-color: rgba(var(--v-theme-accent), 0.55);
  background: rgba(var(--v-theme-accent), 0.14);
}

.task-form__calendar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
}

.task-form__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
</style>
